import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { CheckCircle2, ArrowRight, Loader2, ShieldCheck, Globe2, Headphones, Lock, Clock, Sparkles } from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import SEOHead from '@/components/marketing/SEOHead';
import PageHeader from '@/components/marketing/PageHeader';
import CTASection from '@/components/marketing/CTASection';
import DecorBackground from '@/components/marketing/DecorBackground';
import CyberOperator from '@/components/marketing/CyberOperator';
import WorldMap from '@/components/marketing/WorldMap';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { api } from '@/lib/api';
import { COUNTRIES_LIST } from '@/data/site-content';

const VERTICALS = ['iGaming', 'Finance / Trading', 'E-commerce', 'Sweepstakes', 'Software & Utilities', 'VPN', 'Nutra / Wellness', 'Lead Generation', 'Other'];
const FORMATS = ['Zero-Click', 'Domain Redirect', 'Pop', 'Push', 'In-Page Push', 'Direct Click / Smart Redirect', 'Not Sure / Need Recommendation'];
const BUDGETS = ['Under $250/day', '$250–$500/day', '$500–$1,000/day', '$1,000–$5,000/day', '$5,000+/day'];
const TRACKERS = ['Voluum', 'RedTrack', 'BeMob', 'Keitaro', 'Binom', 'CPV Lab', 'Google Analytics', 'Postback / S2S only', 'Other'];

const initialState = {
  fullName: '', companyName: '', email: '', contactHandle: '', country: '',
  website: '', offerUrls: '', mainVertical: '', trafficFormat: '',
  estimatedDailyBudget: '', targetGeos: '', trackingPlatform: '',
  monthlyAdSpend: '', preferredLaunchDate: '', needsManagerSupport: false,
  message: '', complianceConfirmed: false, contactConsent: false,
  companyWebsiteHp: '',
};

export default function SignupPage() {
  const [form, setForm] = useState(initialState);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const seoPath = location.pathname === '/apply' ? '/apply' : '/signup';

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  function validate() {
    const e = {};
    if (!form.fullName.trim()) e.fullName = 'Required';
    if (!form.companyName.trim()) e.companyName = 'Required';
    if (!form.email.trim() || !/^\S+@\S+\.\S+$/.test(form.email)) e.email = 'Valid email required';
    if (!form.contactHandle.trim()) e.contactHandle = 'Required';
    if (!form.country) e.country = 'Required';
    if (!form.offerUrls.trim()) e.offerUrls = 'Provide at least one offer URL';
    if (!form.mainVertical) e.mainVertical = 'Required';
    if (!form.trafficFormat) e.trafficFormat = 'Required';
    if (!form.estimatedDailyBudget) e.estimatedDailyBudget = 'Required';
    if (!form.targetGeos.trim()) e.targetGeos = 'Required';
    if (!form.trackingPlatform) e.trackingPlatform = 'Required';
    if (!form.complianceConfirmed) e.complianceConfirmed = 'Required';
    if (!form.contactConsent) e.contactConsent = 'Required';
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  async function onSubmit(ev) {
    ev.preventDefault();
    if (!validate()) {
      toast.error('Please fix the highlighted fields.');
      return;
    }
    setSubmitting(true);
    try {
      await api.post('/leads/advertiser-signup', form);
      setSuccess(true);
      toast.success('Application received.');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Submission failed. Please try again.';
      toast.error(typeof msg === 'string' ? msg : 'Submission failed.');
    } finally {
      setSubmitting(false);
    }
  }

  if (success) {
    return (
      <>
        <SEOHead path={seoPath} title="Application Received" description="Your advertiser application has been submitted to Ads Dashbound. Our team will review and get back to you." />
        <PageHeader breadcrumbs={[{ label: 'Apply as Advertiser' }]} eyebrow="Thank you" title="Application received — we’ll be in touch." subtitle="Our team will review your traffic requirements and contact you with onboarding next steps. You’ll typically hear back within 1–3 business days." />
        <section className="bg-[#F5F7FB]">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
            <motion.div
              initial={{ opacity: 0, scale: 0.96, y: 12 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
              className="relative rounded-2xl bg-white border border-slate-900/10 p-8 shadow-[0_24px_60px_rgba(2,6,23,0.10)] overflow-hidden"
            >
              <div
                className="absolute -top-32 left-1/2 -translate-x-1/2 h-64 w-64 rounded-full pointer-events-none"
                style={{ background: 'radial-gradient(circle, rgba(16,185,129,0.18), transparent 70%)' }}
                aria-hidden
              />
              <div className="flex items-center gap-3">
                <motion.span
                  initial={{ scale: 0.5, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ delay: 0.15, duration: 0.5, type: 'spring', stiffness: 200, damping: 18 }}
                  className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-[#10B981]/10 text-[#10B981] border border-[#10B981]/30 shadow-[0_0_28px_rgba(16,185,129,0.35)]"
                >
                  <CheckCircle2 className="h-6 w-6" />
                </motion.span>
                <h2 className="font-display text-[24px] font-bold tracking-[-0.025em] text-slate-900">Application Received</h2>
              </div>
              <p className="mt-4 text-[15.5px] text-slate-700 leading-relaxed">
                Our team will review your traffic requirements and contact you shortly.
              </p>
              <ol className="mt-6 space-y-3 text-[15px] text-slate-600 list-decimal pl-5">
                <li>A reviewer checks your offers, GEOs and budget band.</li>
                <li>You receive a recommendation on formats, daily caps and source rules.</li>
                <li>If approved, your account manager guides the top-up and launch.</li>
              </ol>
              <div className="mt-7 flex flex-col sm:flex-row gap-3">
                <Button asChild className="btn-primary-gradient text-white rounded-xl py-6 px-6 border-0" data-testid="signup-success-home-button">
                  <Link to="/" className="inline-flex items-center gap-2">Return to homepage <ArrowRight className="h-4 w-4" /></Link>
                </Button>
                <Button asChild variant="outline" className="rounded-xl transition-colors" data-testid="signup-success-contact-button">
                  <Link to="/contact">Contact support</Link>
                </Button>
              </div>
            </motion.div>
          </div>
        </section>
      </>
    );
  }

  return (
    <>
      <SEOHead path={seoPath} />
      <PageHeader
        breadcrumbs={[{ label: 'Apply as Advertiser' }]}
        eyebrow="Advertiser Application"
        title="Get approved and start routing premium traffic."
        subtitle="Tell us about your offers, target GEOs, and traffic goals. Our team reviews every application and recommends formats and source rules before you launch."
      />
      {/* Two-column layout: left benefits/trust panel, right form */}
      <section className="relative bg-[#050B17] text-white isolate overflow-hidden">
        <DecorBackground variant="section" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="grid lg:grid-cols-12 gap-10">
            {/* Benefits panel */}
            <aside className="lg:col-span-5 space-y-5">
              {/* Cyber operator showcase with world-map backdrop + floating cards */}
              <div className="relative rounded-2xl bg-gradient-to-b from-white/[0.06] to-white/[0.02] border border-[#7DDCFF]/20 backdrop-blur-xl overflow-hidden h-[400px]">
                <div className="absolute inset-0 opacity-[0.32] pointer-events-none" aria-hidden>
                  <WorldMap intensity="high" showLabels={false} />
                </div>
                <CyberOperator />
                {/* Floating: Application Review */}
                <div className="absolute left-3 bottom-3 z-20 inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-[#0B172A]/90 border border-[#34D399]/30 backdrop-blur-md text-[11.5px] text-slate-200 shadow-[0_18px_40px_rgba(0,0,0,0.4)]">
                  <span className="h-2 w-2 rounded-full bg-[#34D399] pulse-glow" />
                  Application Review
                </div>
                {/* Floating: Campaign Fit Check */}
                <div className="absolute right-3 bottom-3 z-20 inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-[#0B172A]/90 border border-[#8B5CF6]/30 backdrop-blur-md text-[11.5px] text-slate-200 shadow-[0_18px_40px_rgba(0,0,0,0.4)]">
                  <span className="h-2 w-2 rounded-full bg-[#8B5CF6]" />
                  Campaign Fit Check
                </div>
              </div>

              {/* Application Flow mini timeline */}
              <div className="rounded-2xl bg-gradient-to-b from-white/[0.05] to-white/[0.02] border border-white/10 p-6 backdrop-blur-xl">
                <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/[0.04] border border-white/10 text-[11px] font-semibold uppercase tracking-[0.18em] text-[#22D3EE]">Application Flow</span>
                <ol className="mt-5 relative">
                  <span className="absolute left-[14px] top-1 bottom-1 w-px" style={{ background: 'linear-gradient(180deg, #FBBF24, #22D3EE, #8B5CF6)' }} aria-hidden />
                  {[
                    { n: 1, t: 'Manual application review', tone: '#FBBF24' },
                    { n: 2, t: 'Traffic-format consultation', tone: '#22D3EE' },
                    { n: 3, t: 'Source-level planning', tone: '#34D399' },
                    { n: 4, t: 'Top-up based launch', tone: '#8B5CF6' },
                  ].map((s) => (
                    <li key={s.n} className="relative pl-10 py-2">
                      <span
                        className="absolute left-0 top-2 inline-flex h-7 w-7 items-center justify-center rounded-full text-[11px] font-bold text-white border"
                        style={{ background: s.tone, borderColor: `${s.tone}66`, boxShadow: `0 0 14px ${s.tone}55` }}
                      >
                        {s.n}
                      </span>
                      <span className="text-[13.5px] text-slate-200">{s.t}</span>
                    </li>
                  ))}
                </ol>
              </div>

              <BenefitItem icon={Globe2} title="150+ GEO targeting possibilities" text="Vertical-aware routing across global tier-1, tier-2 and tier-3 markets." />
              <BenefitItem icon={ShieldCheck} title="Continuous quality controls" text="Bot filtering, source monitoring and anti-fraud reviews on every campaign." />
              <BenefitItem icon={Headphones} title="Hands-on launch support" text="Most approved advertisers go live within 1–3 business days with managed help during the first 72 hours." />
              <BenefitItem icon={Clock} title="Top-up funded campaigns" text="No subscription plans. Approved advertisers top up balance based on traffic goals and format selection." />

              <div className="rounded-2xl bg-[#0B172A] border border-white/10 p-5">
                <div className="flex items-start gap-3">
                  <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-[#22D3EE]/10 text-[#22D3EE] border border-[#22D3EE]/30 shrink-0"><Lock className="h-4 w-4" /></span>
                  <p className="text-[13px] text-slate-300/90 leading-relaxed">
                    <strong className="text-white font-semibold">Privacy:</strong> Your details are reviewed by the Ads Dashbound team. We do not share applicant information with third parties.
                  </p>
                </div>
              </div>
            </aside>

            {/* Form card */}
            <div className="lg:col-span-7">
              <form
                onSubmit={onSubmit}
                data-testid="advertiser-signup-form"
                className="relative rounded-2xl bg-gradient-to-b from-white/[0.05] to-white/[0.02] border border-white/10 p-6 sm:p-8 backdrop-blur-xl shadow-[0_30px_80px_rgba(0,0,0,0.4)] space-y-9 overflow-hidden"
                noValidate
              >
                <div
                  className="absolute top-0 left-0 right-0 h-px"
                  style={{ background: 'linear-gradient(90deg, transparent, rgba(34,211,238,0.55), rgba(124,58,237,0.55), transparent)' }}
                  aria-hidden
                />
                {/* Honeypot */}
                <input
                  type="text"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  name="company-website"
                  data-testid="advertiser-signup-company-website-field"
                  value={form.companyWebsiteHp}
                  onChange={(e) => set('companyWebsiteHp', e.target.value)}
                  style={{ position: 'absolute', left: '-9999px', height: 0, width: 0, opacity: 0 }}
                />

                <Section number="01" title="Contact details">
                  <Field label="Full name *" error={errors.fullName}><DarkInput data-testid="signup-fullname-input" value={form.fullName} onChange={(e) => set('fullName', e.target.value)} placeholder="Jane Doe" /></Field>
                  <Field label="Company name *" error={errors.companyName}><DarkInput data-testid="signup-company-input" value={form.companyName} onChange={(e) => set('companyName', e.target.value)} placeholder="Acme Performance" /></Field>
                  <Field label="Work email *" error={errors.email}><DarkInput data-testid="signup-email-input" type="email" value={form.email} onChange={(e) => set('email', e.target.value)} placeholder="jane@acme.com" /></Field>
                  <Field label="Telegram / Skype / WhatsApp *" error={errors.contactHandle}><DarkInput data-testid="signup-contact-input" value={form.contactHandle} onChange={(e) => set('contactHandle', e.target.value)} placeholder="@jane_acme" /></Field>
                  <Field label="Country *" error={errors.country}>
                    <Select value={form.country} onValueChange={(v) => set('country', v)}>
                      <SelectTrigger data-testid="signup-country-select" className="bg-[#0B172A] border-white/10 text-white"><SelectValue placeholder="Select country" /></SelectTrigger>
                      <SelectContent className="max-h-72">{COUNTRIES_LIST.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                    </Select>
                  </Field>
                  <Field label="Website (optional)"><DarkInput data-testid="signup-website-input" value={form.website} onChange={(e) => set('website', e.target.value)} placeholder="https://your-company.com" /></Field>
                </Section>

                <Section number="02" title="Campaign details">
                  <Field label="Main vertical *" error={errors.mainVertical}>
                    <Select value={form.mainVertical} onValueChange={(v) => set('mainVertical', v)}>
                      <SelectTrigger data-testid="signup-vertical-select" className="bg-[#0B172A] border-white/10 text-white"><SelectValue placeholder="Choose vertical" /></SelectTrigger>
                      <SelectContent>{VERTICALS.map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}</SelectContent>
                    </Select>
                  </Field>
                  <Field label="Traffic format interested in *" error={errors.trafficFormat}>
                    <Select value={form.trafficFormat} onValueChange={(v) => set('trafficFormat', v)}>
                      <SelectTrigger data-testid="signup-format-select" className="bg-[#0B172A] border-white/10 text-white"><SelectValue placeholder="Select traffic format" /></SelectTrigger>
                      <SelectContent>{FORMATS.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
                    </Select>
                  </Field>
                  <Field label="Estimated daily budget *" error={errors.estimatedDailyBudget}>
                    <Select value={form.estimatedDailyBudget} onValueChange={(v) => set('estimatedDailyBudget', v)}>
                      <SelectTrigger data-testid="signup-budget-select" className="bg-[#0B172A] border-white/10 text-white"><SelectValue placeholder="Choose budget band" /></SelectTrigger>
                      <SelectContent>{BUDGETS.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
                    </Select>
                  </Field>
                  <Field label="Tracking platform *" error={errors.trackingPlatform}>
                    <Select value={form.trackingPlatform} onValueChange={(v) => set('trackingPlatform', v)}>
                      <SelectTrigger data-testid="signup-tracking-select" className="bg-[#0B172A] border-white/10 text-white"><SelectValue placeholder="Choose tracker" /></SelectTrigger>
                      <SelectContent>{TRACKERS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                    </Select>
                  </Field>
                  <Field label="Target GEOs *" error={errors.targetGeos} full>
                    <DarkInput data-testid="signup-geos-input" value={form.targetGeos} onChange={(e) => set('targetGeos', e.target.value)} placeholder="e.g. DE, FR, IT, ES — or describe your GEO mix" />
                  </Field>
                  <Field label="Offer URLs (one per line) *" error={errors.offerUrls} full>
                    <DarkTextarea data-testid="signup-offers-textarea" rows={4} value={form.offerUrls} onChange={(e) => set('offerUrls', e.target.value)} placeholder={'https://your-offer.com/lp1\nhttps://your-offer.com/lp2'} />
                  </Field>
                </Section>

                <Section number="03" title="Optional">
                  <Field label="Current monthly ad spend"><DarkInput data-testid="signup-monthly-spend-input" value={form.monthlyAdSpend} onChange={(e) => set('monthlyAdSpend', e.target.value)} placeholder="$50,000" /></Field>
                  <Field label="Preferred launch date"><DarkInput data-testid="signup-launch-date-input" value={form.preferredLaunchDate} onChange={(e) => set('preferredLaunchDate', e.target.value)} placeholder="Within 1–2 weeks" /></Field>
                  <Field label="Need account manager support?" full>
                    <label className="inline-flex items-center gap-2 text-[14px] text-slate-200">
                      <Checkbox
                        data-testid="signup-manager-checkbox"
                        checked={!!form.needsManagerSupport}
                        onCheckedChange={(v) => set('needsManagerSupport', !!v)}
                      />
                      Yes, I’d like a managed launch
                    </label>
                  </Field>
                  <Field label="Message / campaign goals" full>
                    <DarkTextarea data-testid="signup-message-textarea" rows={4} value={form.message} onChange={(e) => set('message', e.target.value)} placeholder="Briefly describe KPIs, scaling goals, or anything we should know." />
                  </Field>
                </Section>

                <div className="space-y-3 pt-4 border-t border-white/10">
                  <label className="flex items-start gap-3 text-[13.5px] text-slate-200 leading-relaxed">
                    <Checkbox
                      data-testid="signup-compliance-checkbox"
                      className="mt-0.5"
                      checked={form.complianceConfirmed}
                      onCheckedChange={(v) => set('complianceConfirmed', !!v)}
                    />
                    <span>
                      I confirm that my offers comply with Ads Dashbound’s advertising policies and do not promote prohibited, deceptive, or illegal content.
                      {errors.complianceConfirmed && <span className="block text-xs text-red-400 mt-1">{errors.complianceConfirmed}</span>}
                    </span>
                  </label>
                  <label className="flex items-start gap-3 text-[13.5px] text-slate-200 leading-relaxed">
                    <Checkbox
                      data-testid="signup-consent-checkbox"
                      className="mt-0.5"
                      checked={form.contactConsent}
                      onCheckedChange={(v) => set('contactConsent', !!v)}
                    />
                    <span>
                      I agree to be contacted by Ads Dashbound regarding my advertiser application.
                      {errors.contactConsent && <span className="block text-xs text-red-400 mt-1">{errors.contactConsent}</span>}
                    </span>
                  </label>
                </div>

                <div className="pt-2">
                  <Button
                    type="submit"
                    disabled={submitting}
                    data-testid="advertiser-signup-submit-button"
                    className="btn-primary-gradient text-white rounded-xl px-7 py-6 text-[15px] font-semibold border-0 w-full sm:w-auto"
                  >
                    {submitting ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Submitting…</> : 'Submit Advertiser Application'}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </section>
      <CTASection eyebrow="Need help?" title="Not sure which format fits?" subtitle="Pick “Not Sure / Need Recommendation” in the form — we’ll guide you to the right setup based on your offers and GEOs." />
    </>
  );
}

function BenefitItem({ icon: Icon, title, text }) {
  return (
    <div className="flex items-start gap-3 rounded-2xl bg-white/[0.03] border border-white/10 p-5 backdrop-blur-xl lift-on-hover">
      <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-[#22D3EE]/15 to-[#7C3AED]/15 border border-white/10 shrink-0">
        <Icon className="h-4.5 w-4.5 text-[#22D3EE]" />
      </span>
      <div>
        <h3 className="font-display text-[15.5px] font-semibold tracking-[-0.02em] text-white">{title}</h3>
        <p className="mt-1 text-[13.5px] text-slate-300/85 leading-relaxed">{text}</p>
      </div>
    </div>
  );
}

function Section({ number, title, children }) {
  return (
    <div>
      <div className="flex items-center gap-3 mb-5">
        <span className="font-display text-[12.5px] font-mono-tabular text-[#22D3EE]">{number}</span>
        <span className="h-px flex-1 bg-white/10" />
        <h2 className="font-display text-[15px] font-semibold tracking-[-0.02em] text-white uppercase">{title}</h2>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">{children}</div>
    </div>
  );
}
function Field({ label, error, full, children }) {
  return (
    <div className={full ? 'sm:col-span-2 space-y-1.5' : 'space-y-1.5'}>
      <Label className="text-[12.5px] font-medium text-slate-300 uppercase tracking-[0.06em]">{label}</Label>
      {children}
      {error && <p className="text-xs text-red-400">{error}</p>}
    </div>
  );
}
function DarkInput(props) {
  return <Input {...props} className="bg-[#0B172A] border-white/10 text-white placeholder:text-slate-500 focus-visible:ring-[#22D3EE]/50" />;
}
function DarkTextarea(props) {
  return <Textarea {...props} className="bg-[#0B172A] border-white/10 text-white placeholder:text-slate-500 focus-visible:ring-[#22D3EE]/50" />;
}
